import { fieldKeyMap, memberSummaryFieldLabels } from '../constants';
import { fieldCategories } from '../constants';
import { MemberSummaryFormState } from '../utils/DataTypes';

export const getAllSummaryFields = () =>
  fieldCategories.flatMap((category) => {
    if (category.fields) {
      return category.fields;
    }
    if (category.groups) {
      return category.groups.flat();
    }
    return [];
  });

export const getBlankSummaryConfig = (): MemberSummaryFormState => ({
  detailed_fields: [],
  rollup_config: {
    group_by_fields: [],
    sum_fields: [],
  },
  pa_status_enabled: false,
  zip_invoices_enabled: false,
});

export const getFullSummaryConfig = (): MemberSummaryFormState => ({
  detailed_fields: fieldCategories.flatMap((category) => {
    if (category.fields) {
      return category.fields;
    }
    if (category.groups) {
      return category.groups.flat();
    }
    return [];
  }),
  rollup_config: {
    group_by_fields: [],
    sum_fields: [],
  },
});

export const formatLabel = (key: string) => {
  const custom = memberSummaryFieldLabels[key];
  if (custom) return custom;

  return key
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) => {
      const upper = word.toUpperCase();
      return ['SSN', 'RX', 'NDC', 'POS'].includes(upper) ? upper : word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

export const areConfigsEqual = (configA: MemberSummaryFormState | null, configB: MemberSummaryFormState | null) => {
  if (!configA || !configB) {
    return configA === configB;
  }
  return JSON.stringify(configA) === JSON.stringify(configB);
};

export const toClientObject = (client: { client_name: string; client_number: string }) => ({
  id: client.client_number,
  client_name: client.client_name,
  client_number: client.client_number,
});

type ArrayDiff = { added: string[]; removed: string[] };
export type FullDiff = {
  detailed: ArrayDiff;
  rollup: {
    groupBy: ArrayDiff;
    sum: ArrayDiff;
  };
  reportViewType: { changed: boolean; from?: string; to?: string };
  paStatus: { changed: boolean; from?: boolean; to?: boolean };
  zipInvoices: { changed: boolean; from?: boolean; to?: boolean };
};

const diffArrays = (prev: string[] = [], curr: string[] = []): { added: string[]; removed: string[] } => {
  const prevSet = new Set(prev);
  const currSet = new Set(curr);
  const added = Array.from(currSet).filter((x) => !prevSet.has(x));
  const removed = Array.from(prevSet).filter((x) => !currSet.has(x));
  return { added, removed };
};

export const getFullConfigDiff = (
  prev: MemberSummaryFormState | null,
  curr: MemberSummaryFormState | null
): FullDiff => {
  const detailed = diffArrays(prev?.detailed_fields, curr?.detailed_fields);

  const groupBy = diffArrays(prev?.rollup_config?.group_by_fields, curr?.rollup_config?.group_by_fields);
  const sum = diffArrays(prev?.rollup_config?.sum_fields, curr?.rollup_config?.sum_fields);

  return {
    detailed,
    rollup: { groupBy, sum },
    reportViewType: {
      changed: prev?.report_view_type !== curr?.report_view_type,
      from: prev?.report_view_type,
      to: curr?.report_view_type,
    },
    paStatus: {
      changed: prev?.pa_status_enabled !== curr?.pa_status_enabled,
      from: prev?.pa_status_enabled,
      to: curr?.pa_status_enabled,
    },
    zipInvoices: {
      changed: prev?.zip_invoices_enabled !== curr?.zip_invoices_enabled,
      from: prev?.zip_invoices_enabled,
      to: curr?.zip_invoices_enabled,
    },
  };
};

export const isConfigBlank = (cfg: MemberSummaryFormState | null | undefined) => {
  if (!cfg) return true;

  const detailedList = Array.isArray(cfg.detailed_fields)
    ? cfg.detailed_fields
    : Object.keys(cfg.detailed_fields ?? {});

  const anyDetailed = detailedList.length > 0;
  const anyGroup = (cfg.rollup_config?.group_by_fields?.length ?? 0) > 0;
  const anySum = (cfg.rollup_config?.sum_fields?.length ?? 0) > 0;

  return !anyDetailed && !anySum;
};

const mapArray = (arr?: string[]) =>
  (arr ?? []).map((uiKey) => fieldKeyMap[uiKey as keyof typeof fieldKeyMap]).filter(Boolean) as string[];

export function buildExtractParms(state: MemberSummaryFormState, reportViewOverride?: 'detailed' | 'rollup') {
  const toBackend = (uiKey: string) => fieldKeyMap[uiKey as keyof typeof fieldKeyMap] || uiKey;

  const detailed_fields = (state.detailed_fields ?? []).map(toBackend);

  const rollup_config = {
    group_by_fields: mapArray(state.rollup_config?.group_by_fields),
    sum_fields: mapArray(state.rollup_config?.sum_fields),
  };

  return {
    rollup_config,
    detailed_fields,
    report_view_type: reportViewOverride ?? (state.report_view_type as 'detailed' | 'rollup') ?? 'detailed',
  };
}

export const reverseFieldKeyMap = Object.fromEntries(
  Object.entries(fieldKeyMap).map(([uiKey, backendKey]) => [backendKey, uiKey])
);

export const transformFetchedConfig = (config: MemberSummaryFormState) => {
  if (!config) return { detailed_fields: [], rollup_config: {} };

  const transformedDetailed = config.detailed_fields?.map((key) => reverseFieldKeyMap[key] || key) ?? [];

  const transformedRollup = config.rollup_config
    ? {
        group_by_fields: config.rollup_config.group_by_fields?.map((key) => reverseFieldKeyMap[key] || key) || [],
        sum_fields: config.rollup_config.sum_fields?.map((key) => reverseFieldKeyMap[key] || key) || [],
      }
    : { group_by_fields: [], sum_fields: [] };

  return {
    detailed_fields: transformedDetailed,
    rollup_config: transformedRollup,
    report_view_type: config.report_view_type,
    file_type: config.file_type,
    delimiter: config.delimiter,
    pa_status_enabled: config.pa_status_enabled ?? false,
    zip_invoices_enabled: config.zip_invoices_enabled ?? false,
  };
};
